import { toIntlLocale } from 'shared/helpers/intlLocale';

export const pad2 = n => String(n).padStart(2, '0');

export const toIsoDate = date => {
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

export const todayIsoDate = () => toIsoDate(new Date());

export const parseIsoDate = iso => {
  if (!iso || typeof iso !== 'string') return null;
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]) - 1,
    day: Number(match[3]),
  };
};

export const parseIsoDateTime = iso => {
  if (!iso || typeof iso !== 'string') return { date: null, time: null };
  const match = iso.match(/^(\d{4}-\d{2}-\d{2})(?:T(\d{2}:\d{2}))?/);
  if (!match) return { date: null, time: null };
  return { date: match[1], time: match[2] || null };
};

export const formatDisplayDate = isoDate => {
  const parsed = parseIsoDate(isoDate);
  if (!parsed) return '';
  return `${pad2(parsed.day)}/${pad2(parsed.month + 1)}/${String(parsed.year).slice(-2)}`;
};

export const formatDisplayDateTime = isoDateTime => {
  const { date, time } = parseIsoDateTime(isoDateTime);
  if (!date) return '';
  const dateLabel = formatDisplayDate(date);
  return time ? `${dateLabel} · ${time}` : dateLabel;
};

export const capitalizeLabel = value => {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const weekdayLabels = locale => {
  const formatter = new Intl.DateTimeFormat(toIntlLocale(locale), {
    weekday: 'narrow',
  });
  // 2024-01-07 is a Sunday — keep week start aligned with existing SiteUP pickers.
  return Array.from({ length: 7 }, (_, index) =>
    formatter.format(new Date(2024, 0, 7 + index))
  );
};

export const monthYearLabel = (year, month, locale) => {
  const raw = new Intl.DateTimeFormat(toIntlLocale(locale), {
    month: 'long',
    year: 'numeric',
  }).format(new Date(year, month, 1));
  return capitalizeLabel(raw);
};

export const buildMonthCells = (year, month) => {
  const first = new Date(year, month, 1);
  const gridStart = new Date(year, month, 1 - first.getDay());
  const today = todayIsoDate();

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(
      gridStart.getFullYear(),
      gridStart.getMonth(),
      gridStart.getDate() + index
    );
    const iso = toIsoDate(date);
    return {
      iso,
      day: date.getDate(),
      outside: date.getMonth() !== month,
      isToday: iso === today,
    };
  });
};

export const buildTimeSlots = (startHour, endHour, stepMinutes) => {
  const slots = [];
  const start = startHour * 60;
  const end = endHour * 60;
  for (let minutes = start; minutes <= end; minutes += stepMinutes) {
    slots.push(`${pad2(Math.floor(minutes / 60))}:${pad2(minutes % 60)}`);
  }
  return slots;
};

export const periodForHour = hour => {
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
};

export const groupSlotsByPeriod = slots => {
  const groups = { morning: [], afternoon: [], evening: [] };
  slots.forEach(slot => {
    groups[periodForHour(Number(slot.slice(0, 2)))].push(slot);
  });
  return groups;
};

export const shiftMonth = (year, month, delta) => {
  const next = new Date(year, month + delta, 1);
  return { year: next.getFullYear(), month: next.getMonth() };
};
